//Library Imports
import { useState, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";

//Local Imports
import ListingItem from "./ListingItem";
import SelectedBottom from "modules/Common/Components/SelectedBottom";
import Checkbox from "../../../modules/Common/Components/Checkbox";
import { RootState } from "store";
import { IUserWorkflowData } from "../interfaces";
import EmptyTabInfo from "modules/Content/Shared/EmptyTabInfo";
import { deleteWorkflow } from "../actions";
import InfiniteLoader from "modules/Common/Components/InfiniteLoader";
import ConfirmDialog from "modules/Common/Components/ConfirmDialog";

interface IListingTable {
  getUserWorkflowsNext: () => void;
  selectedWorkflowIds: number[];
  setSelectedWorkflowIds: (value: number[]) => void;
  clearSelectedWorkflowIds: () => void;
}

const ListingTable: React.FC<IListingTable> = (props: IListingTable) => {
  // Redux States
  const deleteWorkflowLoading: boolean = useSelector(
    (state: RootState) => state.automation.deleteWorkflow.loading
  );
  const userWorkflowsLoading: boolean = useSelector((state: RootState) => state.automation.userWorkflows.loading);
  const userWorkflowsData: IUserWorkflowData[] = useSelector(
    (state: RootState) => state.automation.userWorkflows.data
  );
  const searchedUserWorkflowsData: IUserWorkflowData[] = useSelector(
    (state: RootState) => state.automation.userWorkflows.searchedData
  );
  const searchTerm: string = useSelector((state: RootState) => state.automation.userWorkflows.searchTerm);
  const filterType: string = useSelector((state: RootState) => state.automation.userWorkflows.filterType);
  const page: number = useSelector((state: RootState) => state.automation.userWorkflows.page);
  const userWorkflowsTotalCount: number = useSelector(
    (state: RootState) => state.automation.userWorkflows.totalCount
  );
  const updateWorkflowLoading: boolean = useSelector(
    (state: RootState) => state.automation.updateWorkflow.loading
  );
  const updateWorkflowError: boolean = useSelector((state: RootState) => state.automation.updateWorkflow.error);

  // Local States
  const [isSearched, setIsSearched] = useState(false);
  const [showError, setShowError] = useState(false);
  const [selectAllFlag, setSelectAllFlag] = useState(false);
  const [excludedWorkflowsIds, setExcludedWorkflowsIds] = useState<number[]>([]);
  const [selectableWorkflowsCount, setSelectableWorkflowsCount] = useState<number>(0);

  // Local Variables
  const { getUserWorkflowsNext, selectedWorkflowIds, setSelectedWorkflowIds } = props;

  // Component Logic
  useEffect(() => {
    clearSelectedWorkflowsHandler();
    if (searchTerm.length > 0 || filterType.length > 0) {
      setIsSearched(true);
    } else {
      setIsSearched(false);
    }
  }, [searchTerm, filterType]);

  // added this useEffect to avoid the following bug
  // If select all workflows from workflows listing and then
  // delete only one workflow from listing,
  // then the overall counter is not updated at bottom of workflows listing
  useEffect(() => {
    if (!deleteWorkflowLoading) {
      const updatedList = userWorkflowsData
        .filter((workflow) => selectedWorkflowIds.includes(workflow.id))
        .map((workflow) => workflow.id);
      setSelectedWorkflowIds(updatedList);
    }
  }, [deleteWorkflowLoading]);

  const selectAllWorkflowIds = (checked: boolean) => {
    if (checked) {
      if (isSearched) {
        setSelectedWorkflowIds(
          searchedUserWorkflowsData
            .filter((workflow) => workflow.status !== "active")
            .map((workflow) => workflow.id)
        );
      } else {
        setSelectedWorkflowIds(
          userWorkflowsData.filter((workflow) => workflow.status !== "active").map((workflow) => workflow.id)
        );
      }
      setSelectAllFlag(true);
    } else {
      setSelectedWorkflowIds([]);
      setExcludedWorkflowsIds([]);
      setSelectAllFlag(false);
    }
  };

  const deleteWorkflowHandler = () => {
    deleteWorkflow(selectedWorkflowIds, isSearched, selectAllFlag, excludedWorkflowsIds, searchTerm, filterType);
    setSelectedWorkflowIds([]);
    setSelectAllFlag(false);
  };

  const clearSelectedWorkflowsHandler = () => {
    setSelectedWorkflowIds([]);
    setExcludedWorkflowsIds([]);
    setSelectAllFlag(false);
  };

  const next = () => {
    getUserWorkflowsNext();
  };

  useEffect(() => {
    if (updateWorkflowError) setShowError(true);
  }, [updateWorkflowError]);

  useEffect(() => {
    const selectableWorkflows = userWorkflowsData.filter((workflow) => workflow.status !== "active").length;
    setSelectableWorkflowsCount(selectableWorkflows);
    if (selectAllFlag) {
      if (isSearched) {
        const selectableWorkflowsLength = searchedUserWorkflowsData.filter(
          (workflow) => workflow.status !== "active"
        ).length;
        setSelectableWorkflowsCount(selectableWorkflowsLength);
        setSelectedWorkflowIds(
          searchedUserWorkflowsData
            .filter(
              (workflow: IUserWorkflowData) =>
                !excludedWorkflowsIds.includes(workflow.id) && workflow.status !== "active"
            )
            .map((workflow: IUserWorkflowData) => workflow.id)
        );
      } else {
        const selectableWorkflowsLength = userWorkflowsData.filter(
          (workflow) => workflow.status !== "active"
        ).length;
        setSelectableWorkflowsCount(selectableWorkflowsLength);
        setSelectedWorkflowIds(
          userWorkflowsData
            .filter(
              (workflow: IUserWorkflowData) =>
                !excludedWorkflowsIds.includes(workflow.id) && workflow.status !== "active"
            )
            .map((workflow: IUserWorkflowData) => workflow.id)
        );
      }
    }
  }, [searchedUserWorkflowsData, userWorkflowsData]);

  return (
    <>
      <Wrapper className="set-table-height-with-footer">
        {/* Toggle class for show/hide footer "set-table-height-with-footer" 
        AND Toggle class on body "gillRoy-index" */}
        <InfiniteLoader
          dataLength={isSearched ? searchedUserWorkflowsData.length + 1 : userWorkflowsData.length + 1}
          next={next}
          hasMore={
            isSearched
              ? userWorkflowsTotalCount > searchedUserWorkflowsData.length
              : userWorkflowsTotalCount > userWorkflowsData.length
          }
          loader={
            userWorkflowsLoading && page > 0 ? <EmptyTabInfo description={"Loading..."}></EmptyTabInfo> : <></>
          }
        >
          <div
            hidden={isSearched ? searchedUserWorkflowsData.length === 0 : userWorkflowsData.length === 0}
            className="label-row"
          >
            Workflows
          </div>

          <table className={deleteWorkflowLoading || updateWorkflowLoading ? "disabled" : ""}>
            <thead hidden={isSearched ? searchedUserWorkflowsData.length === 0 : userWorkflowsData.length === 0}>
              <tr>
                <td>
                  <Checkbox
                    checked={
                      isSearched
                        ? searchedUserWorkflowsData.length > 0 &&
                          selectableWorkflowsCount === selectedWorkflowIds.length
                        : userWorkflowsData.length > 0 && selectableWorkflowsCount === selectedWorkflowIds.length
                    }
                    onChange={(e: any) => selectAllWorkflowIds(e.target.checked)}
                    disabled={
                      (userWorkflowsLoading && page === 0) ||
                      deleteWorkflowLoading ||
                      updateWorkflowLoading ||
                      filterType === "active"
                    }
                  />
                </td>
                <td></td>
                <td>Name</td>
                <td></td>
              </tr>
            </thead>
            <tbody>
              {userWorkflowsLoading && page === 0 ? (
                <EmptyTabInfo description={"Loading..."}></EmptyTabInfo>
              ) : isSearched ? (
                searchedUserWorkflowsData.length > 0 ? (
                  searchedUserWorkflowsData.map((userWorkflow: IUserWorkflowData) => (
                    <ListingItem
                      key={userWorkflow.id}
                      workflowId={userWorkflow.id}
                      name={userWorkflow.name}
                      color={userWorkflow.status === "active" ? "#1EC252" : "#CACACA"}
                      status={userWorkflow.status.toLowerCase()}
                      selectedWorkflowIds={selectedWorkflowIds}
                      setSelectedWorkflowIds={setSelectedWorkflowIds}
                      isSearched={true}
                      selectAllFlag={selectAllFlag}
                      setSelectAllFlag={setSelectAllFlag}
                      excludedWorkflowsIds={excludedWorkflowsIds}
                      setExcludedWorkflowsIds={setExcludedWorkflowsIds}
                    />
                  ))
                ) : (
                  <EmptyTabInfo description={"No Workflows matched against your search"}></EmptyTabInfo>
                )
              ) : userWorkflowsData.length > 0 ? (
                userWorkflowsData.map((userWorkflow: IUserWorkflowData) => (
                  <ListingItem
                    key={userWorkflow.id}
                    workflowId={userWorkflow.id}
                    name={userWorkflow.name}
                    color={userWorkflow.status === "active" ? "#1EC252" : "#CACACA"}
                    status={userWorkflow.status.toLowerCase()}
                    selectedWorkflowIds={selectedWorkflowIds}
                    setSelectedWorkflowIds={setSelectedWorkflowIds}
                    isSearched={false}
                    selectAllFlag={selectAllFlag}
                    setSelectAllFlag={setSelectAllFlag}
                    excludedWorkflowsIds={excludedWorkflowsIds}
                    setExcludedWorkflowsIds={setExcludedWorkflowsIds}
                  />
                ))
              ) : (
                <EmptyTabInfo
                  description={
                    "Your Workflows will show here, please click on the “New Workflow +” button at the top to start building your workflow"
                  }
                ></EmptyTabInfo>
              )}
            </tbody>
          </table>
        </InfiniteLoader>
        <SelectedBottom
          onClearSelection={clearSelectedWorkflowsHandler}
          selectedCount={selectedWorkflowIds.length}
          selectableWorkflowsCount={selectableWorkflowsCount}
          actionText="Delete"
          confirmTitle={`${
            selectAllFlag && excludedWorkflowsIds && excludedWorkflowsIds.length === 0
              ? selectableWorkflowsCount
              : selectAllFlag && excludedWorkflowsIds && excludedWorkflowsIds.length > 0
              ? selectableWorkflowsCount - excludedWorkflowsIds.length
              : selectedWorkflowIds.length
          } selected Workflow(s) will be deleted.`}
          askConfirmDelete={true}
          actionHandler={deleteWorkflowHandler}
          loading={deleteWorkflowLoading}
          selectAllFlag={selectAllFlag}
          setSelectAllFlag={setSelectAllFlag}
          excludedWorkflowsIds={excludedWorkflowsIds}
        />
      </Wrapper>
      {showError ? (
        <DialogWrapper>
          <ConfirmDialog
            confirm={"Okay"}
            onConfirm={() => setShowError(false)}
            title={"Sommething went wrong!"}
            titleQuestion={null}
          />
        </DialogWrapper>
      ) : null}
    </>
  );
};

export default ListingTable;

const DialogWrapper = styled.div`
  z-index: 17;
  top: 50%;
  left: 50%;
  position: fixed;
`;

const Wrapper = styled.div`
  &.set-table-height-with-footer {
    padding-bottom: 85px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      padding-bottom: 65px;
    }
  }
  .disabled {
    pointer-events: none;
    opacity: 0.5;
  }
  .floating-footer {
    z-index: 9;
  }
  table {
    width: 100%;
    table-layout: fixed;
    thead {
      td {
        color: #5e7079;
        font-weight: 500;
        font-size: 16px;
        font-family: "Roboto", sans-serif;
      }
    }
    tr {
      border-bottom: 1px solid #dddddd;
      td {
        padding: 16px 0;
        &:nth-child(1) {
          padding-left: 16px;
          padding-right: 32px;
          width: 20px;
        }
        &:nth-child(2) {
          width: 110px;
          padding-left: 0;
        }
        &:nth-child(3) {
          padding-left: 35px;
        }
        &:nth-child(4) {
          padding-left: 16px;
          padding-right: 40px;
          width: 20px;
        }
      }
    }
  }
`;
